/**
 * Frontend permission helper — UX convenience ONLY.
 * The backend re-checks every permission (see docs/security.md). Never rely on
 * this for security.
 */

export type RoleName =
  | "OWNER"
  | "ADMIN"
  | "MANAGER"
  | "MEDIA_BUYER"
  | "CREATIVE"
  | "ACCOUNTANT"
  | "VIEWER";

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  roles: RoleName[];
  /** flattened permission keys, e.g. ["clients.view", "payments.create"] */
  permissions: string[];
}

/** Owner/Admin bypass: treated as having every permission. */
const SUPER_ROLES: RoleName[] = ["OWNER", "ADMIN"];

/** Roles that see the full agency dashboard, can assign tasks and manage the team. */
const AGENCY_ADMIN_ROLES: RoleName[] = ["OWNER", "ADMIN", "MANAGER"];

export function can(user: CurrentUser | null | undefined, permission: string): boolean {
  if (!user) return false;
  if (user.roles.some((r) => SUPER_ROLES.includes(r))) return true;
  return user.permissions.includes(permission);
}

export function canAny(user: CurrentUser | null | undefined, permissions: string[]): boolean {
  return permissions.some((p) => can(user, p));
}

/** True for OWNER / ADMIN / MANAGER — those who see the full agency dashboard. */
export function isAgencyAdmin(user: CurrentUser | null | undefined): boolean {
  if (!user) return false;
  return user.roles.some((r) => AGENCY_ADMIN_ROLES.includes(r));
}

/**
 * Mock current user for Phase 2/3 (no backend yet). Owner sees everything.
 * Phase 5 replaces this with the authenticated user from `/auth/me`.
 */
export const MOCK_USER: CurrentUser = {
  id: "u_owner",
  name: "Yacine Amrani",
  email: "yacine@fadagency.dz",
  roles: ["OWNER"],
  permissions: [],
};
